/**
 * mb-timeline.js — BASIS. Mango Butter
 * Canvas-rendered scroll-driven glowing timeline line.
 * Multi-layer blur passes create a glossy, wide-spreading glow
 * matching the brand's sunset-orange palette.
 */

(function () {
  'use strict';

  /* ─── Canvas dimensions ───────────────────────────────────────
     220px wide so the ambient halo has room to spread.
     Center line sits at x=110.  ─────────────────────────────── */
  const CANVAS_W = 220;
  const HALF_W   = 110;

  /* ─── Init ────────────────────────────────────────────────────── */
  function initTimeline() {
    const wrap       = document.querySelector('[data-tl-wrap]');
    const entries    = document.querySelector('[data-tl-entries]');
    const canvas     = document.querySelector('[data-tl-canvas]');
    const allEntries = document.querySelectorAll('[data-tl-entry]');

    if (!wrap || !entries || !canvas) return;

    const ctx = canvas.getContext('2d');

    /* ── Pulse state for tip orb ─────────────────────────────── */
    let tipPulse    = 0;
    let tipDir      = 1;
    let rafId       = null;
    let progress    = 0;

    /* ── Size canvas + align it with the dot column ─────────── */
    function layoutCanvas() {
      canvas.width  = CANVAS_W;
      canvas.height = entries.offsetHeight;

      const firstDot = entries.querySelector('.mb-tl-dot-bg');
      const wrapRect  = wrap.getBoundingClientRect();
      if (firstDot) {
        const r   = firstDot.getBoundingClientRect();
        const cx  = r.left + r.width / 2 - wrapRect.left;
        canvas.style.left = (cx - HALF_W) + 'px';
      }
    }

    /* ─────────────────────────────────────────────────────────────
       DRAW — four layered passes + a pulsing tip orb
       ───────────────────────────────────────────────────────────── */
    function draw(p, pulse) {
      const W     = CANVAS_W;
      const H     = canvas.height;
      const cx    = HALF_W;
      const fillH = H * p;

      ctx.clearRect(0, 0, W, H);

      /* ── Static track (always visible) ───────────────────── */
      const tg = ctx.createLinearGradient(0, 0, 0, H);
      tg.addColorStop(0,    'rgba(205,196,184,0.00)');
      tg.addColorStop(0.05, 'rgba(205,196,184,0.42)');
      tg.addColorStop(0.95, 'rgba(205,196,184,0.42)');
      tg.addColorStop(1,    'rgba(205,196,184,0.00)');
      ctx.beginPath();
      ctx.moveTo(cx, 0); ctx.lineTo(cx, H);
      ctx.strokeStyle = tg;
      ctx.lineWidth   = 2.5;
      ctx.stroke();

      if (p < 0.012) return;

      /* Helper: build a linear gradient for the fill segment */
      function fillGrad(stops) {
        const g = ctx.createLinearGradient(0, 0, 0, fillH);
        stops.forEach(([t, c]) => g.addColorStop(t, c));
        return g;
      }

      /* ── Pass 1 ─ Ultra-wide ambient halo (blurred) ───────
         Mimics the wide color bleed seen in the reference button.
         blur(32px) on a 20px stroke → halo spreads ~100px each side */
      ctx.save();
      ctx.filter = 'blur(32px)';
      const halo = fillGrad([
        [0.00, 'rgba(255,130, 40,0.00)'],
        [0.03, 'rgba(255,130, 40,0.72)'],
        [0.55, 'rgba(216,125, 74,0.75)'],
        [1.00, 'rgba(155, 77, 42,0.70)'],
      ]);
      ctx.beginPath();
      ctx.moveTo(cx, 0); ctx.lineTo(cx, fillH);
      ctx.strokeStyle = halo;
      ctx.lineWidth   = 20;
      ctx.stroke();
      ctx.restore();

      /* ── Pass 2 ─ Mid glow ring ────────────────────────────
         blur(12px) on a 10px stroke → tight-medium spread     */
      ctx.save();
      ctx.filter = 'blur(12px)';
      const mid = fillGrad([
        [0.00, 'rgba(255,180, 80,0.00)'],
        [0.03, 'rgba(255,190,100,0.95)'],
        [0.50, 'rgba(216,125, 74,0.96)'],
        [1.00, 'rgba(155, 77, 42,0.90)'],
      ]);
      ctx.beginPath();
      ctx.moveTo(cx, 0); ctx.lineTo(cx, fillH);
      ctx.strokeStyle = mid;
      ctx.lineWidth   = 10;
      ctx.stroke();
      ctx.restore();

      /* ── Pass 3 ─ Inner glow / bright edge ────────────────
         blur(3px) — subtly softens the edge of the core     */
      ctx.save();
      ctx.filter = 'blur(3px)';
      const inner = fillGrad([
        [0.00, 'rgba(255,230,150,0.00)'],
        [0.02, 'rgba(255,230,150,1.00)'],
        [0.25, 'rgba(244,162, 97,1.00)'],
        [0.65, 'rgba(216,125, 74,1.00)'],
        [1.00, 'rgba(155, 77, 42,1.00)'],
      ]);
      ctx.beginPath();
      ctx.moveTo(cx, 0); ctx.lineTo(cx, fillH);
      ctx.strokeStyle = inner;
      ctx.lineWidth   = 6;
      ctx.lineCap     = 'round';
      ctx.stroke();
      ctx.restore();

      /* ── Pass 4 ─ Glossy specular core ────────────────────
         Near-white hot center fading to amber — gives the
         polished / glossy look of the reference button.     */
      ctx.save();
      const specLen = Math.min(fillH, H * 0.55);
      const spec = ctx.createLinearGradient(0, 0, 0, specLen);
      spec.addColorStop(0.00, 'rgba(255,255,250,0.00)');
      spec.addColorStop(0.01, 'rgba(255,255,248,0.95)');
      spec.addColorStop(0.18, 'rgba(255,240,200,0.80)');
      spec.addColorStop(0.50, 'rgba(255,220,160,0.45)');
      spec.addColorStop(1.00, 'rgba(255,200,120,0.00)');

      ctx.beginPath();
      ctx.moveTo(cx, 0); ctx.lineTo(cx, fillH);
      ctx.strokeStyle = spec;
      ctx.lineWidth   = 2;
      ctx.lineCap     = 'round';
      ctx.stroke();
      ctx.restore();

      /* ── Tip orb ───────────────────────────────────────────
         Three concentric layers + pulsing scale for a live
         "burning ember" effect at the leading edge.         */
      const tipY = fillH;
      const ps   = 1 + pulse * 0.38;   // pulse scale 1 → 1.38

      /* Layer A — wide outer bloom (blurred radial) */
      ctx.save();
      ctx.filter = 'blur(14px)';
      const orbA = ctx.createRadialGradient(cx, tipY, 0, cx, tipY, 42 * ps);
      orbA.addColorStop(0.0, `rgba(255,150, 50,${0.60 + pulse * 0.20})`);
      orbA.addColorStop(0.4, `rgba(220,120, 60,${0.35 + pulse * 0.10})`);
      orbA.addColorStop(1.0,  'rgba(216,100, 50,0.00)');
      ctx.beginPath();
      ctx.arc(cx, tipY, 42 * ps, 0, Math.PI * 2);
      ctx.fillStyle = orbA;
      ctx.fill();
      ctx.restore();

      /* Layer B — mid orb (light blur) */
      ctx.save();
      ctx.filter = 'blur(5px)';
      const orbB = ctx.createRadialGradient(cx, tipY, 0, cx, tipY, 16 * ps);
      orbB.addColorStop(0.0, `rgba(255,225,145,${0.98})`);
      orbB.addColorStop(0.3, `rgba(244,162, 97,${0.88})`);
      orbB.addColorStop(1.0,  'rgba(216,125, 74,0.00)');
      ctx.beginPath();
      ctx.arc(cx, tipY, 16 * ps, 0, Math.PI * 2);
      ctx.fillStyle = orbB;
      ctx.fill();
      ctx.restore();

      /* Layer C — sharp bright core dot */
      ctx.save();
      ctx.shadowColor = 'rgba(255,245,210,0.95)';
      ctx.shadowBlur  = 8;
      ctx.beginPath();
      ctx.arc(cx, tipY, 5 * ps, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,245,1.00)';
      ctx.fill();
      ctx.restore();
    }

    /* ── rAF loop (runs while line is active) ────────────────── */
    function loop() {
      tipPulse += tipDir * 0.020;
      if (tipPulse >= 1) { tipPulse = 1; tipDir = -1; }
      if (tipPulse <= 0) { tipPulse = 0; tipDir =  1; }
      draw(progress, tipPulse);
      rafId = requestAnimationFrame(loop);
    }
    function startLoop() { if (!rafId) rafId = requestAnimationFrame(loop); }
    function stopLoop()  { if (rafId)  { cancelAnimationFrame(rafId); rafId = null; } }

    /* ── Scroll progress (framer-motion offset match) ────────── */
    let sTick = false;

    function calcProgress() {
      const rect = entries.getBoundingClientRect();
      const vh   = window.innerHeight;
      const num  = vh * 0.10 - rect.top;
      const den  = (rect.bottom - rect.top) - vh * 0.40;
      const p    = den > 0 ? Math.max(0, Math.min(1, num / den)) : 0;
      progress   = p;
      if (p > 0.012) startLoop(); else { stopLoop(); draw(0, 0); }
      sTick = false;
    }

    function onScroll() {
      if (!sTick) { requestAnimationFrame(calcProgress); sTick = true; }
    }

    /* ── Mark section so CSS fade-in is safe ─────────────────── */
    const sectionEl = document.querySelector('[data-tl-section]');
    if (sectionEl) sectionEl.classList.add('js-tl-ready');

    window.addEventListener('scroll', onScroll, { passive: true });
    layoutCanvas();
    calcProgress();

    /* ── Resize ──────────────────────────────────────────────── */
    let rTimer;
    window.addEventListener('resize', function () {
      clearTimeout(rTimer);
      rTimer = setTimeout(function () { layoutCanvas(); draw(progress, tipPulse); }, 120);
    });

    /* ── Entry fade-in via IntersectionObserver ──────────────── */
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver(
        function (list) {
          list.forEach(function (e) {
            if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      );
      allEntries.forEach(function (el) { obs.observe(el); });
    } else {
      allEntries.forEach(function (el) { el.classList.add('is-visible'); });
    }

    /* ── Active dot + year highlight ─────────────────────────── */
    function updateDots() {
      const trigger = window.innerHeight * 0.62;
      allEntries.forEach(function (entry) {
        const r = entry.getBoundingClientRect();
        entry.classList.toggle('is-active', r.top < trigger && r.bottom > 0);
      });
    }
    window.addEventListener('scroll', updateDots, { passive: true });
    updateDots();
  }

  /* ── Boot ────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTimeline);
  } else {
    initTimeline();
  }

})();
